import React from "react";
import { useTranslation } from "react-i18next";
import { CheckIcon, InfoIcon, SpinnerIcon } from "./icons";
import { BotCheckState } from "./types";

export interface BotCheckStepProps {
  botCheckState: BotCheckState;
  onChange: (next: BotCheckState) => void;
  /**
   * True while a reCAPTCHA token is being minted for the just-clicked checkbox (the request
   * that resolves `botCheckState` to "checked"/"failed"). Distinct from `botCheckState` itself,
   * which only flips once that request settles — without this, ticking the box shows nothing
   * until the (multi-round-trip) reCAPTCHA challenge finishes, which reads as a broken click.
   */
  isVerifying?: boolean;
}

export const BotCheckStep: React.FC<BotCheckStepProps> = ({
  botCheckState,
  onChange,
  isVerifying = false,
}) => {
  const { t } = useTranslation("flightAlerts");

  return (
    <>
      {/* Figma spec: Lexend Deca, Bold 700, 28px/32px, -1px letter-spacing,
          ink/100 (#0F151A) — same on mobile and desktop (DASH-6077). */}
      <h1 className="font-lexend text-[28px]/[32px] font-bold tracking-[-1px] text-ink-100">
        {t("botCheck.title")}
      </h1>

      <div className="flex flex-col gap-4">
        {botCheckState === "failed" ? (
          <>
            {/* Figma spec: card rounded-2xl (16px), 2px ink/12 border,
                bg/card, padding 20px mobile / 32px desktop, gap 16px mobile /
                20px desktop (DASH-6077). */}
            <div className="flex flex-col gap-4 md:gap-5 rounded-2xl border-2 border-ink-12 bg-card p-5 md:p-8">
              {/* Figma spec: rounded-xl (12px), padding 12px, gap 8px,
                  danger/tint background (DASH-6077). */}
              <div className="flex items-start gap-2 rounded-xl bg-danger-tint p-3">
                <InfoIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-danger-100" />
                {/* Figma spec: Lexend Deca, Regular 400, 14px/18px, 0
                    letter-spacing, danger/ink (DASH-6077). */}
                <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-danger-ink">
                  {t("botCheck.failedMessage")}
                </p>
              </div>
              {/* Figma spec: rounded-2xl (16px), fixed 56px height,
                  primary-100/brand-blue background (DASH-6077). */}
              <button
                type="button"
                onClick={() => onChange("pending")}
                className="flex h-14 items-center justify-center rounded-2xl bg-brand-blue"
              >
                {/* Figma spec: Lexend Deca, Bold 700, 16px/20px, 0
                    letter-spacing, white (DASH-6077). */}
                <span className="font-lexend text-[16px]/[20px] font-bold tracking-normal text-white">
                  {t("botCheck.retryButton")}
                </span>
              </button>
            </div>
            {/* Figma spec: Lexend Deca, Regular 400, 10px/14px mobile,
                12px/16px desktop, 0 letter-spacing, ink/55 (DASH-6077). */}
            <p className="font-lexend text-[10px]/[14px] md:text-[12px]/[16px] font-normal tracking-normal text-ink-55">
              {t("botCheck.failedCaption")}
            </p>
          </>
        ) : (
          /* Figma spec: card rounded-2xl (16px), 2px ink/12 border, bg/card,
             padding 20px mobile / 32px desktop, gap 12px mobile / 16px
             desktop (DASH-6077). */
          <label className="flex flex-col gap-3 md:gap-4 rounded-2xl border-2 border-ink-12 bg-card p-5 md:p-8 cursor-pointer">
            <span className="flex items-center gap-3">
              {/* Figma spec: 28x28, rounded-lg (8px), 2px ink/12 border,
                  bg/card. Checked state (not in this Figma frame):
                  brand-blue fill with a white check icon (DASH-6077). */}
              <span className="relative inline-flex h-7 w-7 shrink-0">
                <input
                  type="checkbox"
                  checked={botCheckState === "checked"}
                  disabled={isVerifying}
                  onChange={() => onChange("checked")}
                  aria-label={t("botCheck.checkboxLabel")}
                  aria-busy={isVerifying}
                  className="peer h-7 w-7 cursor-pointer appearance-none rounded-lg border-2 border-ink-12 bg-card checked:border-brand-blue checked:bg-brand-blue disabled:cursor-not-allowed"
                />
                {isVerifying ? (
                  <SpinnerIcon className="pointer-events-none absolute inset-0 m-auto h-4 w-4 animate-spin text-ink-40" />
                ) : (
                  <CheckIcon className="pointer-events-none absolute inset-0 m-auto h-4 w-4 text-white opacity-0 peer-checked:opacity-100" />
                )}
              </span>
              {/* Figma spec: Lexend Deca, Regular 400, 16px/20px, -0.1px
                  letter-spacing, ink/100 (DASH-6077). */}
              <span className="font-lexend text-[16px]/[20px] font-normal tracking-[-0.1px] text-ink-100">
                {t("botCheck.checkboxLabel")}
              </span>
            </span>
            {/* Figma spec: Lexend Deca, Regular 400, 12px/16px, 0
                letter-spacing, ink/60 (DASH-6077). */}
            <span className="font-lexend text-[12px]/[16px] font-normal tracking-normal text-ink-60">
              {isVerifying ? t("botCheck.verifyingLabel") : t("botCheck.checkboxSubtext")}
            </span>
          </label>
        )}
      </div>
    </>
  );
};
